import React from 'react';
import { Row, Col, Card, Avatar, Typography, Divider } from 'antd';
import { Link } from 'react-router-dom';
import { Domain, EntityType, RecommendationModule as RecommendationModuleType } from '../../types.generated';
import { ReactComponent as IconDataDomain } from '../../images/icon-data-domain.svg';
import { ReactComponent as IconDataEntity } from '../../images/icon-data-entity.svg';
import { ReactComponent as IconDataProduct } from '../../images/icon-data-product.svg';
import { useEntityRegistry } from '../useEntityRegistry';
import { useTranslation } from '../../i18n/useTranslation';
import { AutoTooltip } from '../entity/shared/components/common/AutoTooltip';

// Using TypeScript interface for prop validation instead of PropTypes
interface DomainCardsProps {
    domainRecommendationModule: RecommendationModuleType;
}

interface DomainWithDataProducts extends Domain {
    dataProducts: {
        total: number;
    };
}

const TruncatedTitle = ({ title, domain }: { title: string; domain: Domain }) => {
    const entityRegistry = useEntityRegistry();

    return (
        <Typography.Title
            level={5}
            className="f-text-medium-content f-text-single-line f-text-semibold f-color-dark-black-s100 mb-2"
            ellipsis
        >
            <AutoTooltip>
                <Link to={entityRegistry.getEntityUrl(EntityType.Domain, domain.urn)} style={{ color: 'inherit' }}>
                    {title}
                </Link>
            </AutoTooltip>
        </Typography.Title>
    );
};

export const DomainCards = ({ domainRecommendationModule }: DomainCardsProps) => {
    const entityRegistry = useEntityRegistry();
    const { t } = useTranslation();
    return (
        <>
            <Divider className="ma-0" />
            <Row className="f-domain-cards-wrapper">
                {domainRecommendationModule?.content?.slice(0, 8).map((domainContent) => {
                    const domain = domainContent.entity as DomainWithDataProducts;
                    if (!domain) return null;

                    const entityCount = domainContent.params?.contentParams?.count || 0;
                    const dataProductCount = domain.dataProducts?.total || 0;
                    const domainName = domain.properties?.name || domain.id || domain.urn;
                    const domainUrl = entityRegistry.getEntityUrl(EntityType.Domain, domain.urn);

                    return (
                        <Col key={domain.urn} xs={24} lg={8} xl={6}>
                            <Card className="f-domain-card f-rounded-corner-0 pa-3">
                                <Row wrap={false} align="middle" className="f-gap-3">
                                    <Col flex="none">
                                        <Link to={domainUrl}>
                                            <Avatar
                                                icon={<IconDataDomain />}
                                                className="f-avatar-default f-avatar-light-blue"
                                            />
                                        </Link>
                                    </Col>
                                    <Col flex="auto">
                                        <TruncatedTitle title={domainName} domain={domain} />
                                        <Row className="f-gap-2" align="middle">
                                            <Col flex="none">
                                                <Row className="f-gap-1" align="middle">
                                                    <Col flex="none">
                                                        <Avatar
                                                            icon={<IconDataEntity />}
                                                            className="f-avatar-small f-avatar-transparent"
                                                        />
                                                    </Col>
                                                    <Col flex="none">
                                                        <Typography.Text className="f-text-medium-content f-text-single-line f-color-dark-black-s70">
                                                            {entityCount} {t('entities')}
                                                        </Typography.Text>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Divider type="vertical" />
                                            <Col flex="none">
                                                <Row className="f-gap-1" align="middle">
                                                    <Col flex="none">
                                                        <Avatar
                                                            icon={<IconDataProduct />}
                                                            className="f-avatar-small f-avatar-transparent"
                                                        />
                                                    </Col>
                                                    <Col flex="none">
                                                        <Typography.Text className="f-text-medium-content f-text-single-line f-color-dark-black-s70">
                                                            {dataProductCount} {t('data products')}
                                                        </Typography.Text>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            <Divider className="ma-0" />
        </>
    );
};
